import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { api } from '../api/snp500'

export const fetchSnp500 = createAsyncThunk('snp500/fetch', () => api.getSnp500())

export const triggerUpdate = createAsyncThunk('snp500/triggerUpdate', () => api.triggerUpdate())

export const pollJobStatus = createAsyncThunk('snp500/pollJobStatus', (jobId) =>
  api.getJobStatus(jobId)
)

export const submitFixes = createAsyncThunk('snp500/submitFixes', (fixes) =>
  api.submitFixes(fixes)
)

export const fetchFixes = createAsyncThunk('snp500/fetchFixes', () => api.getFixes())

export const updateFix = createAsyncThunk(
  'snp500/updateFix',
  ({ original, replacement }) => api.updateFix(original, replacement)
)

export const deleteFix = createAsyncThunk('snp500/deleteFix', async (original) => {
  await api.deleteFix(original)
  return original
})

export const fetchNoDataSymbols = createAsyncThunk('snp500/fetchNoDataSymbols', () =>
  api.getNoDataSymbols()
)

export const submitNoDataFix = createAsyncThunk(
  'snp500/submitNoDataFix',
  ({ original, replacement }) =>
    api.submitFixes([{ original, replacement, source: 'manual' }])
)

const snp500Slice = createSlice({
  name: 'snp500',
  initialState: {
    items: [],
    loading: false,
    error: null,
    s3LastUpdated: null,
    jobId: null,
    jobStatus: null,
    jobError: null,
    unresolved: [],
    fixes: [],
    fixesLoading: false,
    fixesError: null,
    noDataSymbols: [],
    noDataLoading: false,
    noDataError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSnp500.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchSnp500.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items
        state.s3LastUpdated = action.payload.s3LastUpdated
      })
      .addCase(fetchSnp500.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
      .addCase(triggerUpdate.fulfilled, (state, action) => {
        state.jobId = action.payload.job_id
        state.jobStatus = 'pending'
      })
      .addCase(pollJobStatus.fulfilled, (state, action) => {
        const { status, unresolved, error } = action.payload
        if (status === 'failed') {
          state.jobStatus = 'failed'
          state.jobError = error
        } else {
          state.jobStatus = status
          state.unresolved = unresolved ?? []
        }
      })
      .addCase(submitFixes.fulfilled, (state) => {
        state.unresolved = []
      })
      .addCase(fetchFixes.pending, (state) => {
        state.fixesLoading = true
        state.fixesError = null
      })
      .addCase(fetchFixes.fulfilled, (state, action) => {
        state.fixesLoading = false
        state.fixes = action.payload.fixes
      })
      .addCase(fetchFixes.rejected, (state, action) => {
        state.fixesLoading = false
        state.fixesError = action.error.message
      })
      .addCase(updateFix.fulfilled, (state, action) => {
        const index = state.fixes.findIndex((f) => f.original === action.payload.original)
        if (index !== -1) state.fixes[index] = action.payload
      })
      .addCase(deleteFix.fulfilled, (state, action) => {
        state.fixes = state.fixes.filter((f) => f.original !== action.payload)
      })
      .addCase(fetchNoDataSymbols.pending, (state) => {
        state.noDataLoading = true
        state.noDataError = null
      })
      .addCase(fetchNoDataSymbols.fulfilled, (state, action) => {
        state.noDataLoading = false
        state.noDataSymbols = action.payload.symbols
      })
      .addCase(fetchNoDataSymbols.rejected, (state, action) => {
        state.noDataLoading = false
        state.noDataError = action.error.message
      })
  },
})

export default snp500Slice.reducer
